import { useEffect, useMemo, useRef, useState } from "react";

import { useCommentsQuery, useMarkCommentsReadMutation, useTaskQuery } from "./api";

/** Keep highlights for the open task, while persisting acknowledgements across openings. */
export function useCommentHighlights(taskKey: string, userId: string | undefined) {
  const comments = useCommentsQuery(taskKey, { refetchOnMountOrArgChange: true });
  const task = useTaskQuery(taskKey);
  const [markRead] = useMarkCommentsReadMutation();
  const [candidates, setCandidates] = useState<Set<string>>(new Set());
  const acknowledged = useRef(new Set<string>());
  const involved = userId !== undefined && (
    task.data?.assignees.some((person) => person.id === userId) === true ||
    task.data?.watchers.some((person) => person.id === userId) === true
  );
  const highlighted = useMemo(() => new Set(involved ? comments.data?.data
    .filter((item) => candidates.has(item.id) && item.authorId !== userId)
    .map((item) => item.id) : []), [involved, comments.data, candidates, userId]);

  useEffect(() => {
    // A mounted RTK subscription may initially contain an older unread snapshot.
    if (!involved || !comments.data || comments.isFetching) return;
    const unread = comments.data.data.filter((item) => item.isUnread && item.authorId !== userId);
    if (unread.length === 0) return;
    setCandidates((current) => {
      const next = new Set(current);
      for (const item of unread) next.add(item.id);
      return next;
    });
    const ids = unread.map((item) => item.id).filter((id) => !acknowledged.current.has(id));
    if (ids.length === 0) return;
    for (const id of ids) acknowledged.current.add(id);
    void markRead({ key: taskKey, ids }).unwrap().catch(() => {
      for (const id of ids) acknowledged.current.delete(id);
    });
  }, [involved, comments.data, comments.isFetching, userId, taskKey, markRead]);

  return { comments, highlighted };
}
